import {ProgressAria} from '_components/ProgressAria';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import dayjs from 'dayjs';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Grid} from '@mui/material';

import {ChartPanelList} from './components/ChartPanelList';
import {MapSelect} from './components/MapSelect';
import {SearchConditions, SearchType} from './types';

interface MainPanelProps {
  searchConditions: SearchConditions | null;
}

export const MainPanel = (props: MainPanelProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const [mapList, setMapList] = useState<string[]>([]);
  const [selectedMaps, setSelectedMaps] = useState<string[]>([]);

  /***
   * X軸生成処理
   */
  const xTimeliles = useCallback(() => {
    if (props.searchConditions === null) return [];

    let datelist: any = [];
    let from_day = new Date(props.searchConditions.selectDateFrom);
    let to_day = new Date(props.searchConditions.selectDateTo);

    switch (props.searchConditions.searchType) {
      case SearchType.WORK:
        return [];
      case SearchType.HOUR:
        for (let d = from_day; d <= to_day; d.setHours(d.getHours() + 1)) {
          datelist.push(dayjs(d).format('YYYY-MM-DD HH'));
        }
        return datelist.sort();
      case SearchType.DAY:
        for (let d = from_day; d <= to_day; d.setDate(d.getDate() + 1)) {
          datelist.push(dayjs(d).format('YYYY-MM-DD'));
        }
        return datelist.sort();
      case SearchType.MONTH:
        let start_month_1day = new Date(from_day.setDate(1));
        let end_month_1day = new Date(to_day.setDate(1));
        for (
          start_month_1day;
          start_month_1day <= end_month_1day;
          start_month_1day.setMonth(start_month_1day.getMonth() + 1)
        ) {
          datelist.push(dayjs(start_month_1day).format('YYYY-MM'));
        }
        return datelist.sort();
    }
  }, [
    props.searchConditions?.selectDateFrom,
    props.searchConditions?.selectDateTo,
    props.searchConditions?.searchType
  ]);

  /**
   * 初期処理
   */
  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  /**
   * 検索処理
   */
  useEffect(() => {
    if (props.searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setMapList([]);

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      // MAP一覧取得
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30110,
        parameters: {
          select_date_from: props.searchConditions.selectDateFrom,
          select_date_to: props.searchConditions.selectDateTo,
          work: !isEmpty(props.searchConditions.work)
            ? '{' + props.searchConditions.work.join(',') + '}'
            : null,
          deviceid: !isEmpty(props.searchConditions.deviceid)
            ? '{' + props.searchConditions.deviceid.join(',') + '}'
            : null,
          judge: props.searchConditions.judge === 9 ? null : props.searchConditions.judge
        },
        name: '部位データ',
        cancelToken: cancelSource.current.token,
        t
      }).then(data => {
        const maps = data.map((item: {map: string}) => item.map);
        setMapList(maps);
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [props.searchConditions]);

  useEffect(() => {
    //最初の１件目を選択状態にする
    setSelectedMaps(0 < mapList.length ? [mapList[0]] : []);
  }, [mapList]);

  return props.searchConditions && 0 < mapList.length ? (
    <ProgressAria startProcess={startProcess} loadingMessage>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={3} md={2}>
          <MapSelect
            mapSeries={mapList}
            selectedValues={selectedMaps}
            setSelectedValues={setSelectedMaps}
          />
        </Grid>
        <Grid item xs={12} sm={9} md={10}>
          <ChartPanelList
            mapList={selectedMaps}
            searchConditions={props.searchConditions}
            xTimeliles={xTimeliles()}
          />
        </Grid>
      </Grid>
    </ProgressAria>
  ) : null;
};
